import React, { useState, useEffect } from 'react';
import { setDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/fire';
import { message } from 'antd';
import { FaFileContract, FaUsers, FaUserTie, FaUpload, FaDownload, FaCheckCircle } from 'react-icons/fa';

const AgreementUpload = ({ isSecAdmin, theme = 'light' }) => {
    const [memberAgreementFile, setMemberAgreementFile] = useState(null);
    const [instructorAgreementFile, setInstructorAgreementFile] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState({ member: null, instructor: null });
    const [errorMessage, setErrorMessage] = useState('');


    const isValidFileType = (file) => {
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        return allowedTypes.includes(file.type);
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file && !isValidFileType(file)) {
            setErrorMessage(`Invalid ${type} file type. Please upload a PDF or Word document.`);
            type === 'member' ? setMemberAgreementFile(null) : setInstructorAgreementFile(null);
            return;
        }
        setErrorMessage('');
        type === 'member' ? setMemberAgreementFile(file) : setInstructorAgreementFile(file);
    };

    const handleUploadAgreement = async (type) => {
        const file = type === "member" ? memberAgreementFile : instructorAgreementFile;

        if (!file) {
            message.error("Please select a file first.");
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
            try {
                const fileData = reader.result;

                await setDoc(doc(db, "agreements", type), {
                    fileName: file.name,
                    fileType: file.type,
                    fileData,
                    timestamp: serverTimestamp(),
                });

                message.success("File uploaded successfully!");
                fetchUploadedFile(type);
            } catch (error) {
                console.error("Error uploading file:", error);
                message.error("Failed to upload file.");
            }
        };

        reader.onerror = () => {
            message.error("Error reading file.");
        };
    };


    const fetchUploadedFile = async (type) => {
        const docRef = doc(db, "agreements", type);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            setUploadedFiles((prev) => ({
                ...prev,
                [type]: docSnap.data(),
            }));
        }
    };


    useEffect(() => {
        fetchUploadedFile("member");
        fetchUploadedFile("instructor");
    }, []);

    return (
        <div className="p-4 pb-8">
            {/* Member Agreement Section */}
            <div className={`shadow rounded-lg border mb-4 ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="bg-blue-500 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
                        <FaUsers />
                        Member Agreement
                    </h3>
                </div>
                <div className="p-4">
                    <div className="mb-4">
                        <label className={`block text-sm sm:text-base font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Select Agreement Document
                        </label>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => handleFileChange(e, 'member')}
                            className={`w-full px-3 py-2 border rounded text-sm sm:text-base focus:border-blue-500 focus:outline-none ${
                                theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            disabled={isSecAdmin}
                        />
                        <p className={`text-xs sm:text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>Accepted formats: PDF, DOC, DOCX</p>
                    </div>

                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleUploadAgreement('member')}
                        disabled={isSecAdmin || !memberAgreementFile}
                    >
                        <FaUpload />
                        Upload Member Agreement
                    </button>

                    {uploadedFiles.member && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                            <div className="flex items-start gap-2">
                                <FaCheckCircle className="text-green-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="font-medium text-gray-800 text-sm">Currently Uploaded:</p>
                                    <p className="text-gray-600 text-sm">{uploadedFiles.member.fileName}</p>
                                    <a
                                        href={uploadedFiles.member.fileData}
                                        download={uploadedFiles.member.fileName}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm mt-1 hover:underline"
                                    >
                                        <FaDownload />
                                        Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Instructor Agreement Section */}
            <div className={`shadow rounded-lg border mb-4 ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="bg-blue-500 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
                        <FaUserTie />
                        Instructor Agreement
                    </h3>
                </div>
                <div className="p-4">
                    <div className="mb-4">
                        <label className={`block text-sm sm:text-base font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Select Agreement Document
                        </label>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => handleFileChange(e, 'instructor')}
                            className={`w-full px-3 py-2 border rounded text-sm sm:text-base focus:border-blue-500 focus:outline-none ${
                                theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            disabled={isSecAdmin}
                        />
                        <p className={`text-xs sm:text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>Accepted formats: PDF, DOC, DOCX</p>
                    </div>

                    <button
                        className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleUploadAgreement('instructor')}
                        disabled={isSecAdmin || !instructorAgreementFile}
                    >
                        <FaUpload />
                        Upload Instructor Agreement
                    </button>

                    {uploadedFiles.instructor && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                            <div className="flex items-start gap-2">
                                <FaCheckCircle className="text-green-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="font-medium text-gray-800 text-sm">Currently Uploaded:</p>
                                    <p className="text-gray-600 text-sm">{uploadedFiles.instructor.fileName}</p>
                                    <a
                                        href={uploadedFiles.instructor.fileData}
                                        download={uploadedFiles.instructor.fileName}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm mt-1 hover:underline"
                                    >
                                        <FaDownload />
                                        Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {errorMessage && (
                <div className="bg-red-50 border border-red-200 p-3 rounded mb-4">
                    <p className="text-red-700 text-sm">{errorMessage}</p>
                </div>
            )}
        </div>
    );
};

export default AgreementUpload;
